<template>
  <v-dialog
    v-model="dialog"
    max-width="900"
    :persistent="loading || generatingKeywords"
  >
    <v-card>
      <v-card-title>
        {{ merchProduct.id ? "Editar" : "Criar" }} Produto
      </v-card-title>
      <v-card-text class="pb-2">
        <v-row>
          <v-col cols="12" sm="6" md="7">
            <v-card outlined rounded="xl" style="position: relative">
              <v-tooltip v-if="selectedImageModel" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    absolute
                    left
                    icon
                    dark
                    top
                    class="ma-2"
                    color="primary"
                    style="z-index: 2"
                    @click.stop
                  >
                    <v-icon>mdi-auto-fix</v-icon>
                  </v-btn>
                </template>
                <span>{{ selectedImageModel }}</span>
              </v-tooltip>
              <v-btn
                absolute
                right
                icon
                dark
                top
                class="ma-2"
                color="primary"
                style="z-index: 2"
                @click.stop="openImageInNewTab"
              >
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
              <v-img
                :src="
                  merchProduct.images[selectedImage]?.url ||
                  merchProduct.images[selectedImage]
                "
                :aspect-ratio="1"
                @click="selectImage"
                class="rounded-xl white"
                contain
              >
                <template v-slot:placeholder>
                  <v-row
                    v-if="
                      !merchProduct.images[selectedImage]?.url ||
                      !merchProduct.images[selectedImage]
                    "
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                    style="cursor: pointer"
                  >
                    Clique para selecionar uma imagem
                  </v-row>
                </template>
              </v-img>
            </v-card>
            <v-slide-group mandatory v-model="selectedImage" show-arrows>
              <v-slide-item
                v-for="(image, index) in merchProduct.images"
                :key="index"
                v-slot="{ active, toggle }"
              >
                <v-card
                  :outlined="!active"
                  @click="toggle"
                  :elevation="active ? 3 : 0"
                  class="mx-2 my-3"
                >
                  <v-hover v-slot:default="{ hover }">
                    <v-img
                      :src="image.url || image"
                      :aspect-ratio="1"
                      height="75px"
                      width="75px"
                      contain
                      class="white"
                    >
                      <v-fade-transition>
                        <v-overlay v-if="hover" absolute>
                          <v-btn
                            icon
                            small
                            color="error"
                            @click.stop="askRemoveImage(index)"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </v-overlay>
                      </v-fade-transition>
                    </v-img>
                  </v-hover>
                </v-card>
              </v-slide-item>
            </v-slide-group>
            <input
              type="file"
              ref="fileInput"
              accept="image/*"
              multiple
              @change="handleFileChange"
              hidden
            />
          </v-col>
          <v-col cols="12" sm="6" md="5">
            <div class="d-flex flex-column h-full">
              <div>
                <v-btn-toggle
                  v-model="merchProduct.type"
                  mandatory
                  color="primary"
                  class="d-flex mb-4"
                >
                  <v-btn value="PRODUCT" class="flex-grow-1" small>
                    <v-icon
                      left
                      :color="
                        merchProduct.type === 'PRODUCT' ? 'primary' : undefined
                      "
                    >
                      mdi-cart
                    </v-icon>
                    Produto
                  </v-btn>
                  <v-btn value="COMBO" class="flex-grow-1" small>
                    <v-icon
                      left
                      :color="
                        merchProduct.type === 'COMBO' ? 'primary' : undefined
                      "
                    >
                      mdi-package-variant-closed
                    </v-icon>
                    Combo
                  </v-btn>
                </v-btn-toggle>

                <v-text-field
                  v-model="merchProduct.name"
                  label="Nome"
                  outlined
                  dense
                />
                <v-text-field
                  v-model="merchProduct.description"
                  label="Descrição"
                  outlined
                />

                <v-combobox
                  v-model="merchProduct.keywords"
                  label="Palavras-chave"
                  placeholder="Ex.: cerveja, lata, gelada"
                  hint="Digite e pressione Enter para adicionar"
                  persistent-hint
                  outlined
                  multiple
                  chips
                  small-chips
                  deletable-chips
                  hide-selected
                  hide-no-data
                  :disabled="loading || generatingKeywords"
                />
                <v-btn
                  color="primary"
                  @click="generateKeywords"
                  :loading="generatingKeywords"
                  :disabled="loading || !(merchProduct.name || '').trim()"
                  small
                  block
                  depressed
                  text
                  class="mb-3"
                >
                  <v-icon small left>mdi-auto-fix</v-icon>
                  Sugerir palavras-chave com IA
                </v-btn>

                <b>Imagem</b>
                <v-btn
                  color="primary"
                  @click="selectImage"
                  :disabled="loading"
                  class="mt-2"
                  block
                  depressed
                >
                  <v-icon left>mdi-upload</v-icon>
                  Selecionar imagem
                </v-btn>
                <v-btn
                  color="primary"
                  @click="openGenerateImageDialog"
                  :disabled="loading"
                  small
                  block
                  depressed
                  text
                  class="mt-2"
                >
                  <v-icon small left>mdi-auto-fix</v-icon>
                  Gerar imagem com IA
                </v-btn>
              </div>
              <v-spacer />

              <v-alert v-if="error" type="error" text>
                {{ error }}
              </v-alert>
              <div class="d-flex align-center">
                <v-btn
                  text
                  @click="close"
                  :disabled="loading || generatingKeywords"
                  depressed
                >
                  Cancelar
                </v-btn>
                <v-spacer />

                <v-btn
                  color="success"
                  @click="save"
                  :loading="loading"
                  :disabled="generatingKeywords"
                  depressed
                >
                  Salvar
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <GenerateImageDialog
      ref="generateImageDialog"
      @confirm="onGeneratedImages"
    />

    <v-dialog v-model="confirmRemoveDialog" max-width="400" persistent>
      <v-card>
        <v-card-title>Remover imagem?</v-card-title>
        <v-card-text>
          Esta imagem será removida do produto. Deseja continuar?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmRemoveDialog = false">Cancelar</v-btn>
          <v-btn color="error" text @click="removeImage">Remover</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import merchProductsService from "@/services/admin/merchProducts";
import ImageConverter from "../../../utils/ImageConverter";
import GenerateImageDialog from "./GenerateImageDialog.vue";

const defaultMerchProduct = () => ({
  name: "",
  description: "",
  images: [],
  type: "PRODUCT",
  keywords: [],
});
export default {
  name: "MerchProductDialog",
  components: { GenerateImageDialog },
  data() {
    return {
      dialog: false,
      merchProduct: defaultMerchProduct(),
      error: null,
      loading: false,
      generatingKeywords: false,
      selectedImage: 1,
      confirmRemoveDialog: false,
      indexToRemove: null,
    };
  },
  computed: {
    selectedImageModel() {
      const image = this.merchProduct.images[this.selectedImage];
      return image?.model || null;
    },
  },
  methods: {
    openImageInNewTab() {
      window.open(
        this.merchProduct.images[this.selectedImage]?.url ||
          this.merchProduct.images[this.selectedImage],
        "_blank"
      );
    },
    open(merchProduct) {
      this.merchProduct = Object.assign(
        defaultMerchProduct(),
        structuredClone(merchProduct || {})
      );
      this.merchProduct.keywords = this.normalizeKeywords(
        this.merchProduct.keywords
      );
      this.error = null;
      this.loading = false;
      this.generatingKeywords = false;
      this.dialog = true;
      this.selectedImage = 0;
    },
    close() {
      this.dialog = false;
      this.merchProduct = defaultMerchProduct();
      this.error = null;
      this.loading = false;
      this.generatingKeywords = false;
    },
    async save() {
      this.loading = true;
      if (this.merchProduct.images.length === 0) {
        this.error = "É necessário adicionar pelo menos uma imagem";
        this.loading = false;
        return;
      }
      try {
        let images = [];
        let merchProduct = null;
        const payload = {
          ...this.merchProduct,
          keywords: this.normalizeKeywords(this.merchProduct.keywords),
        };
        if (this.merchProduct.id) {
          let response = await merchProductsService.updateMerchProduct(
            this.merchProduct.id,
            {
              ...payload,
              images: this.merchProduct.images.filter((image) => !image?.file),
            }
          );
          merchProduct = response.merchProduct;
        } else {
          let response = await merchProductsService.createMerchProduct({
            ...payload,
            images: undefined,
          });
          merchProduct = response.merchProduct;
        }

        for (const image of this.merchProduct.images) {
          if (image?.file) {
            const url = await this.uploadImage(merchProduct.id, image.file);
            images.push(url);
          } else images.push(image);
        }

        this.$emit(this.merchProduct.id ? "updated" : "created", {
          ...merchProduct,
          images,
        });
        this.close();
      } catch (error) {
        this.error = error.message || "Erro ao salvar o produto";
      } finally {
        this.loading = false;
      }
    },
    async uploadImage(merchProductId, file) {
      const imageConverter = new ImageConverter();
      const optimizedFile = await imageConverter.webp(file, {
        maxWidth: 512,
        maxHeight: 512,
        allowTransparency: false,
        backgroundColor: "#FFFFFF",
      });

      const { url } = await merchProductsService.uploadMerchProductImage(
        merchProductId,
        optimizedFile
      );
      return url;
    },
    selectImage() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const files = event.target.files;
      for (const file of files) {
        const url = URL.createObjectURL(file);
        this.merchProduct.images.push({ url, file });
      }
      this.selectedImage = this.merchProduct.images.length + 1;
    },
    openGenerateImageDialog() {
      this.$refs.generateImageDialog.open({
        name: this.merchProduct.name,
        description: this.merchProduct.description,
      });
    },
    normalizeKeywords(keywords) {
      if (!Array.isArray(keywords)) return [];
      const seen = new Set();
      const normalized = [];
      for (const keyword of keywords) {
        const value =
          typeof keyword === "string"
            ? keyword.trim()
            : String(keyword || "").trim();
        if (!value) continue;
        const key = value.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        normalized.push(value);
      }
      return normalized;
    },
    async generateKeywords() {
      if (!(this.merchProduct.name || "").trim()) {
        this.error = "Informe o nome do produto para gerar palavras-chave";
        return;
      }
      this.generatingKeywords = true;
      this.error = null;
      try {
        const currentKeywords = this.normalizeKeywords(
          this.merchProduct.keywords
        );
        const response =
          await merchProductsService.generateMerchProductKeywords({
            name: this.merchProduct.name,
            description: this.merchProduct.description,
            type: this.merchProduct.type,
            keywords: currentKeywords,
          });
        const suggested = this.normalizeKeywords(response.keywords);
        const existingLower = new Set(
          currentKeywords.map((keyword) => keyword.toLowerCase())
        );
        this.merchProduct.keywords = [
          ...currentKeywords,
          ...suggested.filter(
            (keyword) => !existingLower.has(keyword.toLowerCase())
          ),
        ];
      } catch (error) {
        this.error = error.message || "Erro ao gerar palavras-chave";
      } finally {
        this.generatingKeywords = false;
      }
    },
    onGeneratedImages(images) {
      for (const item of images) {
        this.merchProduct.images.push({
          url: item.url,
          file: item.file,
          model: item.model || null,
        });
      }
      if (images.length)
        this.selectedImage = this.merchProduct.images.length + 1;
    },
    askRemoveImage(index) {
      this.indexToRemove = index;
      this.confirmRemoveDialog = true;
    },
    removeImage() {
      if (this.indexToRemove !== null) {
        this.merchProduct.images.splice(this.indexToRemove, 1);
        this.selectedImage = Math.max(
          1,
          Math.min(this.selectedImage, 1 + this.merchProduct.images.length)
        );
        this.indexToRemove = null;
      }
      this.confirmRemoveDialog = false;
    },
  },
  mounted() {
    this.$parent.$on("open-merch-product-dialog", this.open);
  },
  beforeDestroy() {
    this.$parent.$off("open-merch-product-dialog", this.open);
  },
};
</script>
